import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import useAacFlags from "hooks/useAacFlags";
import { AccountContext } from "views/AccountWrapper";
import { AccessResult, NO_ACCESS_MESSAGE, hasAtLeastOneRequiredAction } from "utils/access";
import { Action, Stack } from "types/generated";
import { AccountViewer } from "components/form/graphql/types";

import useHasStackAdminAccess from "./useHasStackAdminAccess";

const DELETE_ACCESS_TYPES = [Action.StackDelete, Action.SpaceAdmin];

const hasDeleteAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(DELETE_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook to determine if the current user can delete a stack
 * @returns A function that takes an optional stack parameter and returns an AccessResult
 * indicating whether the user has permission to delete the stack
 */
const useCanDeleteStack = (): ((stack?: Stack) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const { customAccessRoles } = useAacFlags();
  const hasAdminAccess = useHasStackAdminAccess();

  return useCallback(
    (stack?: Stack) => {
      if (!stack) {
        return { result: false };
      }

      if (viewer.admin) {
        return { result: true };
      }

      if (hasAdminAccess(stack)) {
        return { result: true };
      }

      // TODO: remove this condition for space id once we have a proper types for queries results
      if (customAccessRoles && hasDeleteAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasAdminAccess, customAccessRoles, viewer]
  );
};

export default useCanDeleteStack;
